import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { PageHeader, Card, Button, Modal, Field, Input, EmptyState, ConfirmModal } from '../components/ui'

type ReservationStatus = 'PENDING' | 'CONFIRMED' | 'SEATED' | 'CANCELLED' | 'NO_SHOW'

const STATUS_LABEL: Record<ReservationStatus, string> = {
  PENDING:   'Pendiente',
  CONFIRMED: 'Confirmada',
  SEATED:    'Sentada',
  CANCELLED: 'Cancelada',
  NO_SHOW:   'No se presentó',
}

const STATUS_COLOR: Record<ReservationStatus, string> = {
  PENDING:   'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  SEATED:    'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW:   'bg-gray-100 text-gray-600',
}

interface Table { id: string; number: number; capacity: number }

interface Reservation {
  id: string
  customerName: string
  phone: string | null
  partySize: number
  reservedAt: string
  notes: string | null
  status: ReservationStatus
  tableId: string | null
  table: Table | null
}

interface Restaurant { venueType: string; reservationsEnabled: boolean }

const EMPTY_FORM = {
  customerName: '',
  phone: '',
  partySize: '2',
  reservedAt: '',
  notes: '',
  tableId: '',
}

function toLocalDatetimeInput(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function toDateString(iso: string) {
  return new Date(iso).toLocaleString('es-MX', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

export default function ReservationsPage() {
  const qc = useQueryClient()
  const [selectedDate, setSelectedDate] = useState(todayISO())
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [editItem, setEditItem] = useState<Reservation | null>(null)
  const [editForm, setEditForm] = useState(EMPTY_FORM)
  const [confirmDelete, setConfirmDelete] = useState<Reservation | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const { data: restaurant } = useQuery<Restaurant>({
    queryKey: ['restaurant-settings'],
    queryFn: async () => (await api.get('/restaurant')).data,
  })

  const { data: tables = [] } = useQuery<Table[]>({
    queryKey: ['tables'],
    queryFn: async () => (await api.get('/tables')).data,
  })

  const dineTables = tables.filter((t: any) => t.type === 'DINE_IN')

  const queryParams = useMemo(() => {
    const params: Record<string, string> = { date: selectedDate }
    if (statusFilter) params['status'] = statusFilter
    return new URLSearchParams(params).toString()
  }, [selectedDate, statusFilter])

  const { data: reservations = [], isLoading } = useQuery<Reservation[]>({
    queryKey: ['reservations', selectedDate, statusFilter],
    queryFn: async () => (await api.get(`/reservations?${queryParams}`)).data,
    enabled: restaurant?.venueType === 'RESTAURANT' && restaurant?.reservationsEnabled,
  })

  function extractApiError(e: unknown): string {
    return (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      ?? (e as Error)?.message
      ?? 'Error desconocido'
  }

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: () => api.post('/reservations', {
      customerName: form.customerName.trim(),
      phone: form.phone.trim() || undefined,
      partySize: Number(form.partySize),
      reservedAt: new Date(form.reservedAt).toISOString(),
      notes: form.notes.trim() || undefined,
      tableId: form.tableId || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reservations'] })
      setOpen(false)
      setForm(EMPTY_FORM)
      setFormError(null)
    },
    onError: (e) => setFormError(extractApiError(e)),
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: () => api.patch(`/reservations/${editItem!.id}`, {
      customerName: editForm.customerName.trim(),
      phone: editForm.phone.trim() || undefined,
      partySize: Number(editForm.partySize),
      reservedAt: new Date(editForm.reservedAt).toISOString(),
      notes: editForm.notes.trim() || undefined,
      tableId: editForm.tableId || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reservations'] })
      setEditItem(null)
      setFormError(null)
    },
    onError: (e) => setFormError(extractApiError(e)),
  })

  const { mutate: changeStatus } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReservationStatus }) =>
      api.patch(`/reservations/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reservations'] }),
  })

  const { mutate: remove } = useMutation({
    mutationFn: (id: string) => api.delete(`/reservations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reservations'] })
      setConfirmDelete(null)
    },
  })

  function openEdit(r: Reservation) {
    setEditItem(r)
    setEditForm({
      customerName: r.customerName,
      phone: r.phone ?? '',
      partySize: String(r.partySize),
      reservedAt: toLocalDatetimeInput(r.reservedAt),
      notes: r.notes ?? '',
      tableId: r.tableId ?? '',
    })
  }

  if (restaurant?.venueType !== 'RESTAURANT') {
    return (
      <div className="p-8">
        <PageHeader title="Reservaciones" subtitle="Solo disponible para establecimientos tipo Restaurante" />
        <EmptyState
          icon="🏨"
          title="Módulo no disponible"
          description="El módulo de reservaciones aplica únicamente para establecimientos de tipo Restaurante."
        />
      </div>
    )
  }

  if (!restaurant?.reservationsEnabled) {
    return (
      <div className="p-8">
        <PageHeader title="Reservaciones" subtitle="Módulo deshabilitado" />
        <EmptyState
          icon="📅"
          title="Reservaciones deshabilitadas"
          description="Activa el módulo en Configuración → Reservaciones para comenzar a usarlo."
        />
      </div>
    )
  }

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Reservaciones"
        subtitle={`${reservations.length} reservación${reservations.length !== 1 ? 'es' : ''} el ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}`}
        action={<Button onClick={() => setOpen(true)}>+ Nueva reservación</Button>}
      />

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">Todos los estados</option>
          {(Object.keys(STATUS_LABEL) as ReservationStatus[]).map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
      </div>

      {/* Lista */}
      {isLoading ? (
        <p className="text-gray-400 text-sm">Cargando…</p>
      ) : reservations.length === 0 ? (
        <EmptyState icon="📅" title="Sin reservaciones" description="No hay reservaciones para este día." />
      ) : (
        <div className="space-y-3">
          {reservations.map((r) => (
            <Card key={r.id}>
              <div className="flex items-start justify-between gap-4">
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{r.customerName}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    🕐 {toDateString(r.reservedAt)} · 👥 {r.partySize} pers.
                    {r.table && ` · Mesa ${r.table.number}`}
                    {r.phone && ` · 📞 ${r.phone}`}
                  </p>
                  {r.notes && (
                    <p className="text-xs text-gray-400 mt-1 italic">"{r.notes}"</p>
                  )}
                </div>

                {/* Acciones rápidas de status */}
                <div className="flex flex-col gap-1.5 shrink-0">
                  {r.status === 'PENDING' && (
                    <button
                      onClick={() => changeStatus({ id: r.id, status: 'CONFIRMED' })}
                      className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Confirmar
                    </button>
                  )}
                  {(r.status === 'PENDING' || r.status === 'CONFIRMED') && (
                    <button
                      onClick={() => changeStatus({ id: r.id, status: 'SEATED' })}
                      className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Sentar
                    </button>
                  )}
                  {(r.status === 'PENDING' || r.status === 'CONFIRMED') && (
                    <button
                      onClick={() => changeStatus({ id: r.id, status: 'NO_SHOW' })}
                      className="text-xs bg-gray-400 text-white px-3 py-1.5 rounded-lg hover:bg-gray-500 transition-colors"
                    >
                      No Show
                    </button>
                  )}
                  <div className="flex gap-1 mt-0.5">
                    <button
                      onClick={() => openEdit(r)}
                      className="text-xs border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => setConfirmDelete(r)}
                      className="text-xs border border-red-100 text-red-500 px-2.5 py-1 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal crear */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nueva reservación">
        <div className="space-y-4">
          <Field label="Nombre del cliente">
            <Input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} placeholder="Juan García" />
          </Field>
          <Field label="Teléfono (opcional)">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+52 55 1234 5678" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Personas">
              <Input type="number" min="1" value={form.partySize} onChange={(e) => setForm({ ...form, partySize: e.target.value })} />
            </Field>
            <Field label="Mesa (opcional)">
              <select
                value={form.tableId}
                onChange={(e) => setForm({ ...form, tableId: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Sin asignar</option>
                {dineTables.map((t) => (
                  <option key={t.id} value={t.id}>Mesa {t.number} (cap. {t.capacity})</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Fecha y hora">
            <input
              type="datetime-local"
              value={form.reservedAt}
              onChange={(e) => setForm({ ...form, reservedAt: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </Field>
          <Field label="Notas (opcional)">
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Celebración, alergias, preferencias…" />
          </Field>
          {formError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => { setOpen(false); setFormError(null) }}>Cancelar</Button>
            <Button onClick={() => create()} disabled={isCreating || !form.customerName || !form.reservedAt || !form.partySize}>
              {isCreating ? 'Guardando…' : 'Crear reservación'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal editar */}
      <Modal open={!!editItem} onClose={() => setEditItem(null)} title="Editar reservación">
        <div className="space-y-4">
          <Field label="Nombre del cliente">
            <Input value={editForm.customerName} onChange={(e) => setEditForm({ ...editForm, customerName: e.target.value })} />
          </Field>
          <Field label="Teléfono (opcional)">
            <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Personas">
              <Input type="number" min="1" value={editForm.partySize} onChange={(e) => setEditForm({ ...editForm, partySize: e.target.value })} />
            </Field>
            <Field label="Mesa (opcional)">
              <select
                value={editForm.tableId}
                onChange={(e) => setEditForm({ ...editForm, tableId: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Sin asignar</option>
                {dineTables.map((t) => (
                  <option key={t.id} value={t.id}>Mesa {t.number} (cap. {t.capacity})</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Fecha y hora">
            <input
              type="datetime-local"
              value={editForm.reservedAt}
              onChange={(e) => setEditForm({ ...editForm, reservedAt: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </Field>
          <Field label="Notas (opcional)">
            <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
          </Field>
          {formError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => { setEditItem(null); setFormError(null) }}>Cancelar</Button>
            <Button onClick={() => update()} disabled={isUpdating || !editForm.customerName || !editForm.reservedAt}>
              {isUpdating ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm delete */}
      <ConfirmModal
        open={!!confirmDelete}
        title="Eliminar reservación"
        message={`¿Eliminar la reservación de ${confirmDelete?.customerName}? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={() => remove(confirmDelete!.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
